import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import AffiliateLayout from '../../components/AffiliateLayout/AffiliateLayout';
import api from '../../services/api';
import {
  PageTitle, PageSub, Head, Card, TableWrap, Table, Btn, Pill, Spinner, Empty,
  NameCell, statusPill, money, colors,
  isStripe, methodLabel, restorableCents, isRestored, payoutStatusText, canRetryPayout,
} from '../../components/AffiliateUI/AffiliateUI';

// Affiliate payout queue. Stripe Connect rows (method 'stripe'): Send with Stripe makes the
// server transfer the money. Older manual rows (Zelle/PayPal/bank): pay outside, then Mark paid.

const fmtDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '-';


// pay_to and admin_notes come resolved from the server (payoutFields.forAdmin handles old rows).
const copyText = (text) => {
  if (!navigator.clipboard?.writeText) {
    toast.error("Couldn't copy, select the text instead");
    return;
  }
  navigator.clipboard.writeText(text)
    .then(() => toast.success('Copied.'))
    .catch(() => toast.error("Couldn't copy, select the text instead"));
};

const FILTERS = [
  { key: 'requested', label: 'Requested' },
  { key: 'processing', label: 'Processing' },
  { key: 'paid', label: 'Paid' },
  { key: 'failed', label: 'Failed' },
  { key: 'reversed', label: 'Reversed' },
  { key: 'restored', label: 'Restored' },
  { key: 'rejected', label: 'Rejected' },
  { key: '', label: 'All' },
];

const AffiliatePayouts = () => {
  const [filter, setFilter] = useState('requested');
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(() => new Set());
  const [notes, setNotes] = useState({});
  const [highlight, setHighlight] = useState(null);
  // Only ZONA admins (role admin) send, retry, release or restore; affiliate admins can reject
  // requested rows. Unknown until /me answers, so those actions stay hidden until then.
  const [fullAdmin, setFullAdmin] = useState(false);
  useEffect(() => {
    api.get('/affiliate-admin/me')
      .then(({ data }) => setFullAdmin(String(data?.user?.role || '').toLowerCase() === 'admin'))
      .catch(() => setFullAdmin(false));
  }, []);
  // A highlighted row stops standing out after 10s.
  useEffect(() => {
    if (!highlight) return undefined;
    const id = setTimeout(() => setHighlight(null), 10000);
    return () => clearTimeout(id);
  }, [highlight]);
  const filterRef = useRef(filter);
  const loadSeq = useRef(0);
  // A failed load keeps the last rows; its toast shows once until a load works again.
  const [loadError, setLoadError] = useState(false);
  const errorToasted = useRef(false);

  // Only the newest request for the current tab may set the rows. A reload of the same tab keeps
  // the rows on screen; only a tab switch (or the first load) shows the spinner.
  const load = ({ reset = false } = {}) => {
    const f = filterRef.current;
    const seq = ++loadSeq.current;
    if (reset) setRows(null);
    api.get('/affiliate-admin/payouts', { params: f ? { status: f } : {} })
      .then(({ data }) => {
        if (seq !== loadSeq.current) return;
        setRows(data.payouts || []);
        setLoadError(false);
        errorToasted.current = false;
      })
      .catch(() => {
        if (seq !== loadSeq.current) return;
        if (!errorToasted.current) toast.error("Couldn't load payouts.");
        errorToasted.current = true;
        setLoadError(true);
        setRows((prev) => prev || []);
      });
  };
  useEffect(() => { filterRef.current = filter; load({ reset: true }); }, [filter]);

  // While any row is processing, reload every 30s so its status and can_retry stay current.
  const anyProcessing = (rows || []).some((r) => r.status === 'processing');
  useEffect(() => {
    if (!anyProcessing) return undefined;
    const id = setInterval(() => load(), 30000);
    return () => clearInterval(id);
  }, [anyProcessing]);

  // After a Stripe send that failed or is still processing, show it on its own tab, highlighted.
  const followRow = (id, status) => {
    if (status !== 'failed' && status !== 'processing') return false;
    setHighlight(id);
    if (filterRef.current !== status && filterRef.current !== '') {
      setFilter(status);
      return true; // the filter effect loads that tab
    }
    return false;
  };

  const setRowBusy = (id, on) =>
    setBusy((prev) => { const next = new Set(prev); if (on) next.add(id); else next.delete(id); return next; });

  const decide = async (r, status) => {
    const who = r.promoter?.name || 'this affiliate';
    const stripe = isStripe(r);
    const retry = r.status === 'processing';
    if (status === 'rejected' && retry) {
      if (!window.confirm(`Release ${money(r.amount_cents)} back to ${who}'s balance? Only do this if Stripe shows no transfer.`)) return;
    } else if (status === 'rejected') {
      if (!window.confirm('Reject this payout request? The affiliate can request again.')) return;
    }
    if (status === 'paid') {
      const ask = retry
        ? `Retry sending ${money(r.amount_cents)} to ${who} with Stripe? Stripe won't send it twice.`
        : stripe
        ? `Send ${money(r.amount_cents)} to ${who} with Stripe? The transfer goes out now.`
        : `Mark ${money(r.amount_cents)} to ${who} as paid? This confirms you sent the money.`;
      if (!window.confirm(ask)) return;
    }
    // A retry re-sends the same request, so it carries no new note.
    const note = retry ? '' : (notes[r._id] || '').trim();
    setRowBusy(r._id, true);
    let newStatus = null;
    // The typed note is cleared once the payout is decided (success, a 409, or a failed transfer the
    // server recorded, e.g. 502 TRANSFER_FAILED); kept on a network error or another 5xx.
    let clearNote = false;
    let foundPaid = false;
    try {
      const body = { status };
      if (note) body.admin_notes = note;
      const { data } = await api.put(`/affiliate-admin/payouts/${r._id}`, body);
      const payout = data?.payout;
      newStatus = payout?.status || null;
      clearNote = true;
      if (newStatus === 'paid') {
        toast.success(stripe
          ? `Sent ${money(r.amount_cents)} with Stripe.${payout.stripe_transfer_id ? ` Transfer ${payout.stripe_transfer_id}.` : ''}`
          : 'Marked as paid.');
      } else if (newStatus === 'rejected') {
        toast.success(retry ? 'Released. The amount is back in their balance.' : 'Payout rejected. The amount is back in their balance.');
      } else {
        toast.info('Still processing. Check again in a few minutes.');
      }
    } catch (err) {
      const data = err?.response?.data;
      newStatus = data?.payout?.status || null;
      clearNote = err?.response?.status === 409 || !!data?.payout;
      if (data?.code === 'TRANSFER_FOUND') {
        // Stripe had already sent it: the server marked it paid.
        foundPaid = true;
        toast.success('Stripe had sent it. Marked paid.');
      } else {
        // The server's own words: not connected, already decided, or the Stripe failure reason.
        toast.error(data?.message || 'Could not update payout.');
      }
    } finally {
      setRowBusy(r._id, false);
      if (clearNote) setNotes((n) => { const next = { ...n }; delete next[r._id]; return next; });
      // Any outcome can change the row (sent, failed, decided elsewhere), so always reload.
      if (foundPaid) {
        setHighlight(r._id);
        if (filterRef.current !== 'paid' && filterRef.current !== '') setFilter('paid');
        else load();
      } else if (!(stripe && followRow(r._id, newStatus))) load();
    }
  };

  // Money Stripe clawed back: put what's left of it back in the affiliate's available balance.
  const restore = async (r) => {
    const who = r.promoter?.name || 'this affiliate';
    const amount = restorableCents(r);
    if (!window.confirm(`Give ${money(amount)} back to ${who}'s available balance?`)) return;
    setRowBusy(r._id, true);
    try {
      await api.put(`/affiliate-admin/payouts/${r._id}/restore`);
      toast.success(`${money(amount)} is back in ${who}'s available balance.`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not restore the balance.');
    } finally {
      setRowBusy(r._id, false);
      load();
    }
  };

  return (
    <AffiliateLayout>
      {!rows ? <Spinner /> : (
            <>
              <Head>
                <div>
                  <PageTitle>Payouts</PageTitle>
                  <PageSub>Stripe requests: Send with Stripe transfers the money to the affiliate's bank. Older manual requests: send the money yourself, then mark paid. No minimum.</PageSub>
                </div>
              </Head>

              <Tabs>
                {FILTERS.map((f) => (
                  <Tab key={f.key} $active={filter === f.key} onClick={() => { setHighlight(null); setFilter(f.key); }}>{f.label}</Tab>
                ))}
              </Tabs>

              <Card>
                <TableWrap>
                  <Table>
                    <thead>
                      <tr>
                        <th>Affiliate</th><th>Contact</th><th className="num">Amount</th>
                        <th>Pay to</th><th>Status</th><th>Requested</th><th className="num">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r._id} style={highlight === r._id ? { background: 'rgba(79,48,234,0.16)' } : undefined}>
                          <td>
                            <NameCell>
                              <span className="n">{r.promoter?.name || 'Unnamed'}</span>
                              {r.promoter?.promo_code && <span className="code">&nbsp;{r.promoter.promo_code}</span>}
                            </NameCell>
                          </td>
                          <td>{r.promoter?.email || r.promoter?.phone || '-'}</td>
                          <td className="num strong">{money(r.amount_cents)}</td>
                          <td>
                            <PayTo>
                              <span className="m">{methodLabel(r.method)}</span>
                              {isStripe(r) ? (
                                r.destination_account && <span className="note">Account {r.destination_account}</span>
                              ) : r.pay_to ? (
                                <span className="d">
                                  {r.pay_to}
                                  <CopyBtn type="button" onClick={() => copyText(r.pay_to)}>Copy</CopyBtn>
                                </span>
                              ) : r.status === 'requested' && <span className="none">No pay-to detail given</span>}
                              {r.stripe_transfer_id && <span className="note">Transfer {r.stripe_transfer_id}</span>}
                              {(r.status === 'failed' || (r.status === 'rejected' && r.failure_reason)) && (
                                <span className="none">{r.status === 'failed' ? 'Failed' : 'Reason'}: {r.failure_reason || 'no reason given'}</span>
                              )}
                              {r.amount_reversed_cents > 0 && (
                                <span className="none">
                                  Reversed {money(r.amount_reversed_cents)}
                                  {r.restored_cents > 0 ? `, restored ${money(r.restored_cents)}` : ''}
                                </span>
                              )}
                              {r.needs_review && (
                                <span className="none">Needs review: Stripe reversed a payout that was already released.</span>
                              )}
                              {r.admin_notes && <span className="note">Note: {r.admin_notes}</span>}
                            </PayTo>
                          </td>
                          <td><Pill tone={isRestored(r) ? 'muted' : statusPill(r.status)}>{payoutStatusText(r)}</Pill></td>
                          <td>{fmtDate(r.requested_at)}</td>
                          <td className="num">
                            {r.status === 'requested' ? (
                              <Decide>
                                <NoteInput
                                  type="text"
                                  placeholder="Note for admins (optional)"
                                  maxLength={500}
                                  value={notes[r._id] || ''}
                                  disabled={busy.has(r._id)}
                                  onChange={(e) => setNotes((n) => ({ ...n, [r._id]: e.target.value }))}
                                />
                                <div style={{ display: 'inline-flex', gap: 8 }}>
                                  {fullAdmin && (
                                    <Btn variant="primary" disabled={busy.has(r._id)} onClick={() => decide(r, 'paid')}>
                                      {isStripe(r) ? 'Send with Stripe' : 'Mark paid'}
                                    </Btn>
                                  )}
                                  <Btn variant="danger" disabled={busy.has(r._id)} onClick={() => decide(r, 'rejected')}>Reject</Btn>
                                </div>
                              </Decide>
                            ) : r.status === 'processing' ? (
                              // Retry or release only once the server allows it (24h after the last attempt).
                              canRetryPayout(r) && fullAdmin ? (
                                <div style={{ display: 'inline-flex', gap: 8 }}>
                                  <Btn variant="primary" disabled={busy.has(r._id)} onClick={() => decide(r, 'paid')}>Retry send</Btn>
                                  <Btn variant="danger" disabled={busy.has(r._id)} onClick={() => decide(r, 'rejected')}>Release</Btn>
                                </div>
                              ) : (
                                <span style={{ color: colors.textSecondary }}>Sending...</span>
                              )
                            ) : restorableCents(r) > 0 && fullAdmin ? (
                              <Btn variant="primary" disabled={busy.has(r._id)} onClick={() => restore(r)}>Restore to balance</Btn>
                            ) : (
                              <span style={{ color: colors.textFaint }}>{r.paid_at ? fmtDate(r.paid_at) : '-'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </TableWrap>
                {rows.length === 0 && (loadError ? (
                  <Empty>
                    Couldn't load payouts.{' '}
                    <Btn variant="primary" onClick={() => load()}>Try again</Btn>
                  </Empty>
                ) : <Empty>No {filter || ''} payouts.</Empty>)}
              </Card>
            </>
      )}
    </AffiliateLayout>
  );
};

export default AffiliatePayouts;

const Tabs = styled.div`display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap;`;
const Tab = styled.button`
  height: 34px; padding: 0 14px; border-radius: 100px; cursor: pointer; font-size: 13px; font-weight: 600;
  border: 1px solid ${({ $active }) => ($active ? 'transparent' : colors.border)};
  background: ${({ $active }) => ($active ? colors.primary : colors.surface1)};
  color: ${({ $active }) => ($active ? '#fff' : colors.textSecondary)};
`;

const PayTo = styled.div`
  display: flex; flex-direction: column; gap: 3px; min-width: 180px;
  .m { font-size: 14px; font-weight: 700; color: #fff; }
  .d { font-size: 14px; color: #fff; word-break: break-all; display: inline-flex; gap: 8px; align-items: center; }
  .none { font-size: 13px; color: ${colors.danger}; }
  .note { font-size: 12px; color: ${colors.textSecondary}; }
`;
const CopyBtn = styled.button`
  border: 1px solid ${colors.border}; background: ${colors.surface1}; color: ${colors.textSecondary};
  border-radius: 6px; font-size: 12px; padding: 2px 8px; cursor: pointer;
`;
const Decide = styled.div`display: inline-flex; flex-direction: column; align-items: flex-end; gap: 8px;`;
const NoteInput = styled.input`
  width: 220px; height: 32px; padding: 0 10px; border-radius: 8px; font-size: 13px;
  border: 1px solid ${colors.border}; background: ${colors.bgInput}; color: #fff;
`;
